// Shown when a new course clashes with existing sessions. Lists each clash with
// its real dates and, when the clash is simply the same course taught by the
// same professor at the same time, offers to add the group to it ("fusion")
// behind an explicit confirmation step.
import React, { useState } from 'react';
import { X, AlertTriangle, Users, DoorOpen, UserCircle, Merge, XCircle, CalendarDays, Check } from 'lucide-react';
import type { ConflictCheckResult, ConflictInfo } from '../../services/scheduleService';

interface ConflictResolutionModalProps {
  conflictResult: ConflictCheckResult;
  onMergeGroups: () => void;
  // Create the series without the clashing weeks (after an explicit confirmation step)
  onSkipConflicts?: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const DAY_NAMES = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MAX_DATES_SHOWN = 4;

const formatDate = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const getConflictIcon = (type: ConflictInfo['type']) => {
  switch (type) {
    case 'PROFESSOR':
      return <UserCircle className="w-5 h-5 text-amber-500" />;
    case 'ROOM':
      return <DoorOpen className="w-5 h-5 text-red-500" />;
    case 'GROUP':
      return <Users className="w-5 h-5 text-orange-500" />;
    default:
      return <AlertTriangle className="w-5 h-5 text-gray-500" />;
  }
};

const getConflictTypeLabel = (type: ConflictInfo['type']) => {
  switch (type) {
    case 'PROFESSOR':
      return 'Professeur déjà occupé';
    case 'ROOM':
      return 'Salle déjà réservée';
    case 'GROUP':
      return 'Groupe déjà en cours';
    default:
      return 'Conflit';
  }
};

const ConflictDates: React.FC<{ dates: string[] }> = ({ dates }) => {
  if (!dates || dates.length === 0) return null;
  const shown = dates.slice(0, MAX_DATES_SHOWN);
  const rest = dates.length - shown.length;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
      {shown.map((d) => (
        <span key={d} className="px-1.5 py-0.5 rounded-md bg-white/70 border border-gray-200 text-[11px] text-gray-700">
          {formatDate(d)}
        </span>
      ))}
      {rest > 0 && <span className="text-[11px] text-gray-500">+{rest} autre{rest > 1 ? 's' : ''}</span>}
    </div>
  );
};

export const ConflictResolutionModal: React.FC<ConflictResolutionModalProps> = ({
  conflictResult,
  onMergeGroups,
  onSkipConflicts,
  onCancel,
  isLoading = false,
}) => {
  const { conflicts, canCombineGroups, suggestedAction, mergeTarget, mergeBlockedReason, skipOption } = conflictResult;
  const [confirmingMerge, setConfirmingMerge] = useState(false);
  const [confirmingSkip, setConfirmingSkip] = useState(false);
  const canSkip = !!onSkipConflicts && !!skipOption && skipOption.remainingCount > 0;
  const skippedCount = skipOption ? skipOption.plannedCount - skipOption.remainingCount : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${confirmingMerge ? 'bg-blue-100' : confirmingSkip ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                {confirmingMerge ? <Merge className="w-5 h-5 text-blue-600" /> : confirmingSkip ? <CalendarDays className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {confirmingMerge ? 'Confirmer la fusion (séance partagée)' : confirmingSkip ? 'Planifier en sautant les conflits' : 'Conflit de planification'}
                </h3>
                <p className="text-sm text-gray-500">
                  {confirmingMerge
                    ? 'Vérifiez la séance que ce groupe va rejoindre'
                    : confirmingSkip
                      ? 'Vérifiez les séances qui ne seront pas planifiées'
                      : `${conflicts.length} conflit${conflicts.length > 1 ? 's' : ''} détecté${conflicts.length > 1 ? 's' : ''} sur les dates réelles`}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {confirmingSkip && skipOption ? (
            <div className="space-y-3">
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 space-y-2 text-sm">
                <p className="font-semibold text-emerald-900">
                  {skipOption.remainingCount} séance{skipOption.remainingCount > 1 ? 's' : ''} sur {skipOption.plannedCount} seront planifiées
                </p>
                <p className="text-emerald-800">
                  {skippedCount} séance{skippedCount > 1 ? 's' : ''} sautée{skippedCount > 1 ? 's' : ''} :
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {skipOption.conflictingDates.map((d) => (
                    <span key={d} className="px-1.5 py-0.5 rounded-md bg-white/80 border border-emerald-200 text-[11px] text-emerald-900">
                      {formatDate(d)}
                    </span>
                  ))}
                </div>
                {skipOption.volumeAfterSkip && (
                  <p className="text-emerald-800 pt-1">
                    Volume horaire après planification : {skipOption.volumeAfterSkip.totalAfterHours}h sur{' '}
                    {skipOption.volumeAfterSkip.volumeHoraire}h prévues
                    {skipOption.volumeAfterSkip.totalAfterHours < skipOption.volumeAfterSkip.volumeHoraire && (
                      <span className="font-medium"> (il manquera {Math.round((skipOption.volumeAfterSkip.volumeHoraire - skipOption.volumeAfterSkip.totalAfterHours) * 10) / 10}h)</span>
                    )}
                  </p>
                )}
              </div>
              <p className="text-sm text-gray-600">
                Les séances sautées ne sont pas reportées : rien n'est ajouté après la dernière semaine, et les
                bornes habituelles (semestre, vacances, périodes d'examens) restent respectées. Vous pourrez les
                placer plus tard, par exemple en rattrapage.
              </p>
            </div>
          ) : confirmingMerge && mergeTarget ? (
            <div className="space-y-3">
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 space-y-2 text-sm">
                <p className="font-semibold text-blue-900">
                  {mergeTarget.moduleElementName}
                  {!mergeTarget.sameSubject && (
                    <span className="ml-2 text-xs font-medium text-blue-700">
                      ({mergeTarget.existingFiliereNames.join(', ') || 'autre filière'})
                    </span>
                  )}
                </p>
                {!mergeTarget.sameSubject && (
                  <p className="text-xs text-blue-800">
                    Matière différente : votre groupe garde sa propre matière, ses heures et sa liste de présence.
                    Les deux groupes suivent la séance ensemble.
                  </p>
                )}
                <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-blue-900/90">
                  <dt className="text-blue-700">Professeur</dt>
                  <dd className="font-medium">{mergeTarget.professorName}</dd>
                  <dt className="text-blue-700">Créneau</dt>
                  <dd className="font-medium">
                    {DAY_NAMES[mergeTarget.dayOfWeek]} {mergeTarget.startTime}-{mergeTarget.endTime}
                  </dd>
                  <dt className="text-blue-700">Salle</dt>
                  <dd className="font-medium">
                    {mergeTarget.roomName ?? 'Aucune salle'}
                    {mergeTarget.roomIdToAssign && (
                      <span className="ml-1 text-xs text-blue-700">(sera affectée aux deux cours)</span>
                    )}
                  </dd>
                  <dt className="text-blue-700">Groupes actuels</dt>
                  <dd className="font-medium">{mergeTarget.existingGroupNames.join(', ') || '—'}</dd>
                  <dt className="text-blue-700">Séances</dt>
                  <dd className="font-medium">
                    {mergeTarget.sessionCount}
                    {mergeTarget.firstDate && mergeTarget.lastDate && (
                      <span className="text-xs text-blue-700"> ({formatDate(mergeTarget.firstDate)} → {formatDate(mergeTarget.lastDate)})</span>
                    )}
                  </dd>
                </dl>
              </div>
              <p className="text-sm text-gray-600">
                Le groupe suivra les séances à venir de ce cours, dans la même salle et avec le même professeur.
                Chaque groupe conserve sa propre liste de présence et son suivi de notes ; le professeur voit les
                deux classes côte à côte et n'est rémunéré qu'une seule fois pour la séance.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {conflicts.map((conflict, index) => (
                  <div
                    key={`${conflict.type}-${conflict.conflictingEntryId}-${index}`}
                    className={`p-4 rounded-xl border ${
                      conflict.type === 'PROFESSOR'
                        ? 'bg-amber-50 border-amber-200'
                        : conflict.type === 'ROOM'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {getConflictIcon(conflict.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{getConflictTypeLabel(conflict.type)}</p>
                        <p className="text-sm text-gray-600 mt-1">{conflict.message}</p>
                        <ConflictDates dates={conflict.dates} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {canCombineGroups && mergeTarget && (
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Merge className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">
                        {mergeTarget.sameSubject ? 'Même cours, même professeur, même heure' : 'Même professeur, même heure'}
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        {suggestedAction || 'Vous pouvez ajouter ce groupe au cours existant plutôt que de créer un doublon.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {canSkip && skipOption && (
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <CalendarDays className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-emerald-900">
                        Planifier quand même en sautant {skippedCount} séance{skippedCount > 1 ? 's' : ''}
                      </p>
                      <p className="text-sm text-emerald-800 mt-1">
                        Les {skipOption.remainingCount} autres séances sont libres : le cours peut être planifié
                        sans les semaines en conflit.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!canCombineGroups && !canSkip && (
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-1">
                  {mergeBlockedReason && (
                    <p className="text-sm font-medium text-gray-800">Fusion impossible : {mergeBlockedReason}</p>
                  )}
                  <p className="text-sm text-gray-600">
                    Ces conflits ne peuvent pas être résolus automatiquement : choisissez un autre créneau, un autre
                    professeur ou une autre salle.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          {confirmingSkip ? (
            <>
              <button
                onClick={() => setConfirmingSkip(false)}
                disabled={isLoading}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors disabled:opacity-50"
              >
                Retour
              </button>
              <button
                onClick={onSkipConflicts}
                disabled={isLoading}
                className="px-4 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors disabled:opacity-50 shadow-sm flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Planification...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Oui, planifier sans ces séances
                  </>
                )}
              </button>
            </>
          ) : confirmingMerge ? (
            <>
              <button
                onClick={() => setConfirmingMerge(false)}
                disabled={isLoading}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors disabled:opacity-50"
              >
                Retour
              </button>
              <button
                onClick={onMergeGroups}
                disabled={isLoading}
                className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50 shadow-sm flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Fusion en cours...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Oui, fusionner
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onCancel}
                disabled={isLoading}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <XCircle size={16} />
                Annuler
              </button>
              {canSkip && (
                <button
                  onClick={() => setConfirmingSkip(true)}
                  disabled={isLoading}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors disabled:opacity-50 shadow-sm flex items-center gap-2"
                >
                  <CalendarDays size={16} />
                  Sauter les conflits
                </button>
              )}
              {canCombineGroups && mergeTarget && (
                <button
                  onClick={() => setConfirmingMerge(true)}
                  disabled={isLoading}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50 shadow-sm flex items-center gap-2"
                >
                  <Merge size={16} />
                  Fusionner les groupes
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;
